/**
 * The drill-down that answers "where did that number come from".
 *
 * The contract formula is shown with its variables bound to the values actually
 * counted, alongside the record count and both parties' measurement methods. This is
 * the screen that distinguishes a computed figure from an asserted one, so nothing on
 * it is decorative.
 */

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Breadcrumb, Notice, Section } from "../components/Chrome";
import { ProvenanceBadge, TargetBadge } from "../components/Badges";
import { metricValue, money, recordCount, shortPeriodLabel, signedGap } from "../lib/format";
import type { Finding, ServiceLevel, Vendor } from "../lib/types";

function VariableTable({ level }: { level: ServiceLevel }) {
  const bound = level.computed?.variables ?? {};
  const rows = Object.entries(level.variable_labels).filter(([key]) => bound[key] !== null && bound[key] !== undefined);

  if (rows.length === 0) {
    return (
      <Notice>
        No variables were bound. This service level has no independent source, so there is
        nothing to recompute and no verified claim rests on the vendor's figure.
      </Notice>
    );
  }

  return (
    <div className="card table-scroll">
      <table>
        <thead>
          <tr>
            <th style={{ width: 60 }}>Var</th>
            <th>What it counts</th>
            <th style={{ textAlign: "right" }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, label]) => (
            <tr key={key}>
              <td className="serif" style={{ color: "var(--gold)", fontSize: 15 }}>
                {key}
              </td>
              <td style={{ color: "var(--parchment)" }}>{label}</td>
              <td className="num serif" style={{ textAlign: "right", fontSize: 15 }}>
                {bound[key]?.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MethodComparison({ level }: { level: ServiceLevel }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
      <div className="card" style={{ padding: 16 }}>
        <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)" }}>
          HOW THE VENDOR MEASURED IT
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.65, margin: "8px 0 0", color: "var(--slate)" }}>
          {level.vendor_method}
        </p>
      </div>
      <div className="card" style={{ padding: 16, borderColor: "rgba(200,155,60,0.4)" }}>
        <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--gold)" }}>
          HOW ACTUOVIOUS MEASURED IT
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.65, margin: "8px 0 0", color: "var(--parchment)" }}>
          {level.our_method}
        </p>
      </div>
    </div>
  );
}

function History({ level }: { level: ServiceLevel }) {
  const data = level.history.map((point) => ({
    period: shortPeriodLabel(point.period),
    Reported: point.reported,
    Computed: point.computed,
    Target: level.expected_target,
  }));

  return (
    <div className="card" style={{ padding: 16, height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -14 }}>
          <CartesianGrid stroke="rgba(239,233,221,0.08)" vertical={false} />
          <XAxis dataKey="period" stroke="var(--slate)" fontSize={11} tickLine={false} />
          <YAxis stroke="var(--slate)" fontSize={11} tickLine={false} domain={["auto", "auto"]} />
          <Tooltip
            contentStyle={{
              background: "var(--ink)",
              border: "1px solid var(--line-strong)",
              borderRadius: 6,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="Reported" stroke="var(--slate)" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="Computed" stroke="var(--gold)" strokeWidth={2} dot={false} />
          <Line
            type="monotone"
            dataKey="Target"
            stroke="var(--rust)"
            strokeWidth={1}
            strokeDasharray="4 4"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ServiceLevelView({
  vendor,
  level,
  finding,
  onBackToPortfolio,
  onBackToVendor,
}: {
  vendor: Vendor;
  level: ServiceLevel;
  finding: Finding | undefined;
  onBackToPortfolio: () => void;
  onBackToVendor: () => void;
}) {
  const creditLines = vendor.credit_lines.filter((line) => line.service_level_id === level.id);

  return (
    <div className="fade-in">
      <Breadcrumb
        items={[
          { label: "Portfolio", onClick: onBackToPortfolio },
          { label: vendor.name, onClick: onBackToVendor },
          { label: level.name },
        ]}
      />

      <Section title={level.name} subtitle={finding?.narrative}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
          }}
        >
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)" }}>
              VENDOR REPORTED
            </div>
            <div className="serif num" style={{ fontSize: 28, color: "var(--slate)", marginTop: 6 }}>
              {metricValue(level.reported?.value, level.unit)}
            </div>
            <div style={{ marginTop: 8 }}>
              <TargetBadge met={level.reported?.met_expected ?? null} />
            </div>
          </div>

          <div className="card" style={{ padding: 16, borderColor: "rgba(200,155,60,0.4)" }}>
            <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--gold)" }}>
              INDEPENDENTLY COMPUTED
            </div>
            <div className="serif num" style={{ fontSize: 28, color: "var(--gold)", marginTop: 6 }}>
              {metricValue(level.computed?.value, level.unit)}
            </div>
            <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <TargetBadge met={level.computed?.met_expected ?? null} />
              <ProvenanceBadge provenance={level.provenance} />
            </div>
          </div>

          {/* A difference and a credit are claims about the vendor. Neither exists
              without an independent figure to set against theirs, and a $0 credit card
              beside a vendor-attested metric reads as "measured, nothing owed" rather
              than "never measured". The guard is here, not only on the route in, so a
              restored link cannot reopen it. */}
          {level.claimable ? (
            <>
              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)" }}>
                  DIFFERENCE
                </div>
                <div
                  className="serif num"
                  style={{
                    fontSize: 28,
                    marginTop: 6,
                    color: (finding?.gap ?? 0) > 3 ? "var(--rust)" : "var(--parchment)",
                  }}
                >
                  {signedGap(finding?.gap)}
                </div>
                <div style={{ fontSize: 11.5, color: "var(--slate)", marginTop: 8 }}>
                  Target {level.direction === "gte" ? "≥" : "≤"} {level.expected_target}
                  {level.unit}
                </div>
              </div>

              <div className="card" style={{ padding: 16 }}>
                <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)" }}>
                  CREDIT THIS PERIOD
                </div>
                <div
                  className="serif num"
                  style={{ fontSize: 28, color: "var(--gold)", marginTop: 6 }}
                >
                  {money(finding?.credit_computed ?? 0)}
                </div>
                <div style={{ fontSize: 11.5, color: "var(--slate)", marginTop: 8 }}>
                  Vendor concedes {money(finding?.credit_reported ?? 0)}
                </div>
              </div>
            </>
          ) : (
            <div className="card" style={{ padding: 16, gridColumn: "span 2" }}>
              <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)" }}>
                NO CLAIM RESTS ON THIS
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.65, margin: "8px 0 0" }}>
                {level.name} is vendor-attested. There is no independent measurement to set
                against the vendor's figure, so the platform reports no difference and
                prices no credit against it. {level.our_method}
              </p>
            </div>
          )}
        </div>
      </Section>

      {level.computed?.evidence_gap && (
        <Section
          title="No independent figure this period"
          subtitle="The evidence did not cover the window, so nothing was computed. That is a different statement from the supplier missing the target, and a different conversation with them."
        >
          <Notice tone="warn">{level.computed.evidence_gap.note}</Notice>
        </Section>
      )}

      <Section
        title="How this was computed"
        subtitle={`The formula as written in the contract, with each variable bound to what was actually counted in ${recordCount(level.computed?.record_count ?? 0)}.`}
      >
        <div
          className="card serif"
          style={{
            padding: "16px 18px",
            fontSize: 16,
            color: "var(--gold)",
            marginBottom: 12,
            letterSpacing: "0.02em",
          }}
        >
          {level.formula_text}
        </div>
        <VariableTable level={level} />
        <div style={{ marginTop: 12 }}>
          <MethodComparison level={level} />
        </div>
      </Section>

      {creditLines.length > 0 && (
        <Section
          title="Credit calculation"
          subtitle={`Under credit model ${vendor.contract.credit_model}, as written in ${vendor.contract.name}.`}
        >
          <div className="card table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Classification</th>
                  <th style={{ textAlign: "right" }}>Base</th>
                  <th style={{ textAlign: "right" }}>Multiplier</th>
                  <th style={{ textAlign: "right" }}>Credit</th>
                  <th>Basis</th>
                </tr>
              </thead>
              <tbody>
                {creditLines.map((line) => (
                  <tr key={`${line.service_level_id}-${line.period}`}>
                    <td style={{ textTransform: "capitalize" }}>
                      {line.default_kind.replace(/_/g, " ")}
                      {/* Model A has no consecutive-run escalator, so there is no run
                          to report and the line is omitted rather than shown as one. */}
                      {line.consecutive_defaults > 0 && (
                        <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 3 }}>
                          {line.consecutive_defaults} consecutive month
                          {line.consecutive_defaults === 1 ? "" : "s"}
                        </div>
                      )}
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {money(line.base_credit)}
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>
                      ×{line.multiplier.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")}
                    </td>
                    <td className="num serif" style={{ textAlign: "right", color: "var(--gold)", fontSize: 15 }}>
                      {money(line.credit)}
                    </td>
                    <td style={{ fontSize: 12, color: "var(--slate)" }}>{line.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {level.next_reset && (
        <Section
          title="At the next annual reset"
          subtitle="The agreement ratchets both targets at the end of each contract year. What that produces is worth knowing before it lands."
        >
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)" }}>
                  EXPECTED TARGET
                </div>
                <div className="serif num" style={{ fontSize: 20, marginTop: 4 }}>
                  {level.expected_target} → {level.next_reset.expected_target}
                  {level.unit}
                </div>
              </div>
              {level.next_reset.minimum_target !== null && (
                <div>
                  <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)" }}>
                    MINIMUM TARGET
                  </div>
                  <div className="serif num" style={{ fontSize: 20, marginTop: 4 }}>
                    {level.minimum_target} → {level.next_reset.minimum_target}
                    {level.unit}
                  </div>
                </div>
              )}
            </div>
            {level.next_reset.minimum_capped && (
              <div style={{ marginTop: 14 }}>
                <Notice tone="warn">{level.next_reset.note}</Notice>
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="Trend" subtitle="Both figures against the contractual target, month by month.">
        <History level={level} />
      </Section>
    </div>
  );
}
